// The names of the active business's members holding a role — its admins by
// default — for a "this is done by an admin: …" line where a manager reaches a
// step only an admin can take (ui-flow F19 D2, F13 UF-09), and for the setup
// pointers' "ask Thandi M or Pieter V" (F19 UF-11, <SetupPointer/>).
//
// Read ONLY for a reader holding `admin:members`, the permission user_tenants'
// tenant-admin SELECT policy checks (mig 20260706170000). Anyone else — every
// floor role — would get nothing but their own row back, so the hook does not
// ask: it returns null, and the caller says "an admin" / "your manager"
// without names. Naming managers to a loader would need a new SECURITY DEFINER
// read; this deliberately does not ask for one.
//
// null = not known (no permission, pending, failed, or nobody holds the role) —
// the caller then says "an admin" without names, never "nobody". The names are
// a hint of whom to ask, never a promise: a custom role or a per-member module
// list can widen or narrow who actually holds a setting.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { usePermissions } from '@/lib/usePermissions'

/** Module-level role lists — they key the cache, so never pass a fresh array. */
export const ADMIN_ROLES = ['ADMIN'] as const
export const MANAGER_AND_ADMIN_ROLES = ['ADMIN', 'MANAGER'] as const

/** The names of this business's members holding any of `roles`, sorted, one
 *  per name. `enabled: false` → null without a read (e.g. a pointer showing a
 *  link, which names nobody). */
export function useBusinessAdmins(
  roles: readonly string[] = ADMIN_ROLES,
  enabled = true,
): string[] | null {
  const tenantId = useActiveTenantId()
  const { can } = usePermissions()
  const allowed = enabled && can('admin', 'members')
  const q = useQuery({
    queryKey: ['business-role-holders', tenantId, roles.join(',')],
    enabled: !!tenantId && allowed,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('user_tenants')
        .select('user_id, users:user_id(name)')
        .eq('tenant_id', tenantId!).in('role', [...roles]).is('revoked_at', null)
      if (error) throw error
      return Array.from(new Set((data ?? [])
        .map((r: { users: { name: string | null } | { name: string | null }[] | null }) =>
          (Array.isArray(r.users) ? r.users[0]?.name : r.users?.name) ?? null)
        .filter((n): n is string => !!n)))
        .sort((a, b) => a.localeCompare(b))
    },
  })
  return allowed && q.data && q.data.length > 0 ? q.data : null
}

/** "an admin of this business (Thandi M, Pieter V)" — names when known. */
export function adminsPhrase(names: string[] | null): string {
  return names && names.length > 0
    ? `an admin of this business (${names.join(', ')})`
    : 'an admin of this business'
}
